/**
 * @author: Claude
 * @date: 2026/03/13
 * @desc: 组件文档页面 - Demo 模块配置
 */

/**
 * Demo 模块配置
 * key: 组件路径
 * value: 动态导入函数
 */
export const DEMO_MODULES: Record<string, () => Promise<{ default: React.ComponentType }>> = {
  Bubble: () => import('@intelligence/v3/components/Bubble/demo'),
  'FormComponents/Select': () => import('@intelligence/v3/components/FormComponents/Select/demo'),
  'FormComponents/SelectMultiple': () => import('@intelligence/v3/components/FormComponents/SelectMultiple/demo'),
  'FormComponents/FilterPresetSelect': () => import('@intelligence/v3/components/FormComponents/FilterPresetSelect/demo'),
  Filter: () => import('@intelligence/v3/components/Filter/demo'),
  'ChartComponents/BarChart': () => import('@intelligence/v3/components/ChartComponents/BarChart/demo'),
  'ChartComponents/HorizontalBarChart': () => import('@intelligence/v3/components/ChartComponents/HorizontalBarChart/demo'),
  'ChartComponents/LineChart': () => import('@intelligence/v3/components/ChartComponents/LineChart/demo'),
  'ChartComponents/MapChart': () => import('@intelligence/v3/components/ChartComponents/MapChart/demo'),
  'ChartComponents/PieChart': () => import('@intelligence/v3/components/ChartComponents/PieChart/demo'),
  'ChartComponents/Tooltip': () => import('@intelligence/v3/components/ChartComponents/Tooltip/demo'),
  'TableComponents/ColumnAligner': () => import('@intelligence/v3/components/TableComponents/ColumnAligner/demo'),
  'TableComponents/GrowthRate': () => import('@intelligence/v3/components/TableComponents/GrowthRate/demo'),
  'TableComponents/MetricTips': () => import('@intelligence/v3/components/TableComponents/MetricTips/demo'),
  'TableComponents/RatioMetricSortBy': () => import('@intelligence/v3/components/TableComponents/RatioMetricSortBy/demo'),
  'TableComponents/SortBy': () => import('@intelligence/v3/components/TableComponents/SortBy/demo'),
  'TableComponents/SortByIcon': () => import('@intelligence/v3/components/TableComponents/SortByIcon/demo'),
};

/**
 * README 模块配置
 * key: 组件路径
 * value: 动态导入函数（返回HTML内容，由markdown-loader处理）
 */
export const README_MODULES: Record<string, () => Promise<any>> = {
  Bubble: () => import('@intelligence/v3/components/Bubble/README.md'),
  'FormComponents/Select': () => import('@intelligence/v3/components/FormComponents/Select/README.md'),
  'FormComponents/SelectMultiple': () => import('@intelligence/v3/components/FormComponents/SelectMultiple/README.md'),
  'FormComponents/FilterPresetSelect': () => import('@intelligence/v3/components/FormComponents/FilterPresetSelect/README.md'),
  Filter: () => import('@intelligence/v3/components/Filter/README.md'),
  'ChartComponents/BarChart': () => import('@intelligence/v3/components/ChartComponents/BarChart/README.md'),
  'ChartComponents/HorizontalBarChart': () => import('@intelligence/v3/components/ChartComponents/HorizontalBarChart/README.md'),
  'ChartComponents/LineChart': () => import('@intelligence/v3/components/ChartComponents/LineChart/README.md'),
  'ChartComponents/MapChart': () => import('@intelligence/v3/components/ChartComponents/MapChart/README.md'),
  'ChartComponents/PieChart': () => import('@intelligence/v3/components/ChartComponents/PieChart/README.md'),
  'ChartComponents/Tooltip': () => import('@intelligence/v3/components/ChartComponents/Tooltip/README.md'),
  'TableComponents/ColumnAligner': () => import('@intelligence/v3/components/TableComponents/ColumnAligner/README.md'),
  'TableComponents/GrowthRate': () => import('@intelligence/v3/components/TableComponents/GrowthRate/README.md'),
  'TableComponents/MetricTips': () => import('@intelligence/v3/components/TableComponents/MetricTips/README.md'),
  'TableComponents/RatioMetricSortBy': () => import('@intelligence/v3/components/TableComponents/RatioMetricSortBy/README.md'),
  'TableComponents/SortBy': () => import('@intelligence/v3/components/TableComponents/SortBy/README.md'),
  'TableComponents/SortByIcon': () => import('@intelligence/v3/components/TableComponents/SortByIcon/README.md'),
};

/**
 * 组件信息接口
 */
export interface IComponentInfo {
  /** 组件名称 */
  name: string;
  /** 组件路径 */
  path: string;
  /** 组件类型 */
  type: 'single' | 'composite';
  /** 是否有 Demo 文件 */
  hasDemo: boolean;
  /** 是否有 README 文件 */
  hasReadme: boolean;
  /** 子组件列表（复合组件时） */
  children?: IComponentInfo[];
}

/**
 * 组件目录树配置
 * 手动维护或通过脚本自动生成
 */
export const COMPONENT_TREE: IComponentInfo[] = [
  // 单独组件
  {
    name: 'Bubble',
    path: 'Bubble',
    type: 'single',
    hasDemo: true,
    hasReadme: true,
  },
  {
    name: 'Filter',
    path: 'Filter',
    type: 'single',
    hasDemo: true,
    hasReadme: true,
  },
  {
    name: 'ChartCard',
    path: 'ChartCard',
    type: 'single',
    hasDemo: false,
    hasReadme: false,
  },
  {
    name: 'DataSourceExplanation',
    path: 'DataSourceExplanation',
    type: 'single',
    hasDemo: false,
    hasReadme: false,
  },
  {
    name: 'DatalabReport',
    path: 'DatalabReport',
    type: 'single',
    hasDemo: false,
    hasReadme: false,
  },
  {
    name: 'GroupByGranularityMetricSelect',
    path: 'GroupByGranularityMetricSelect',
    type: 'single',
    hasDemo: false,
    hasReadme: false,
  },
  {
    name: 'IconButton',
    path: 'IconButton',
    type: 'single',
    hasDemo: false,
    hasReadme: false,
  },
  {
    name: 'MetricExplanationTips',
    path: 'MetricExplanationTips',
    type: 'single',
    hasDemo: false,
    hasReadme: false,
  },
  {
    name: 'MultiImageRenderer',
    path: 'MultiImageRenderer',
    type: 'single',
    hasDemo: false,
    hasReadme: false,
  },
  {
    name: 'ReusePageTemplate',
    path: 'ReusePageTemplate',
    type: 'single',
    hasDemo: false,
    hasReadme: false,
  },
  {
    name: 'ReuseTabPageTemplate',
    path: 'ReuseTabPageTemplate',
    type: 'single',
    hasDemo: false,
    hasReadme: false,
  },
  // 复合组件目录 - ChartComponents
  {
    name: 'ChartComponents',
    path: 'ChartComponents',
    type: 'composite',
    hasDemo: false,
    hasReadme: false,
    children: [
      { name: 'BarChart', path: 'ChartComponents/BarChart', type: 'single', hasDemo: true, hasReadme: true },
      {
        name: 'HorizontalBarChart',
        path: 'ChartComponents/HorizontalBarChart',
        type: 'single',
        hasDemo: true,
        hasReadme: true,
      },
      { name: 'LineChart', path: 'ChartComponents/LineChart', type: 'single', hasDemo: true, hasReadme: true },
      { name: 'MapChart', path: 'ChartComponents/MapChart', type: 'single', hasDemo: true, hasReadme: true },
      { name: 'PieChart', path: 'ChartComponents/PieChart', type: 'single', hasDemo: true, hasReadme: true },
      { name: 'Tooltip', path: 'ChartComponents/Tooltip', type: 'single', hasDemo: true, hasReadme: true },
    ],
  },
  // 复合组件目录 - TableComponents
  {
    name: 'TableComponents',
    path: 'TableComponents',
    type: 'composite',
    hasDemo: false,
    hasReadme: false,
    children: [
      {
        name: 'ColumnAligner',
        path: 'TableComponents/ColumnAligner',
        type: 'single',
        hasDemo: true,
        hasReadme: true,
      },
      { name: 'GrowthRate', path: 'TableComponents/GrowthRate', type: 'single', hasDemo: true, hasReadme: true },
      { name: 'MetricTips', path: 'TableComponents/MetricTips', type: 'single', hasDemo: true, hasReadme: true },
      {
        name: 'RatioMetricSortBy',
        path: 'TableComponents/RatioMetricSortBy',
        type: 'single',
        hasDemo: true,
        hasReadme: true,
      },
      { name: 'SortBy', path: 'TableComponents/SortBy', type: 'single', hasDemo: true, hasReadme: true },
      { name: 'SortByIcon', path: 'TableComponents/SortByIcon', type: 'single', hasDemo: true, hasReadme: true },
    ],
  },
  // 复合组件目录 - FormComponents
  {
    name: 'FormComponents',
    path: 'FormComponents',
    type: 'composite',
    hasDemo: false,
    hasReadme: false,
    children: [
      { name: 'Select', path: 'FormComponents/Select', type: 'single', hasDemo: true, hasReadme: true },
      { name: 'SelectMultiple', path: 'FormComponents/SelectMultiple', type: 'single', hasDemo: true, hasReadme: true },
      { name: 'FilterPresetSelect', path: 'FormComponents/FilterPresetSelect', type: 'single', hasDemo: true, hasReadme: true },
    ],
  },
];

/**
 * 检查组件是否有 Demo
 */
export function hasComponentDemo(path: string): boolean {
  return !!DEMO_MODULES[path];
}

/**
 * 获取 Demo 加载函数
 */
export function getDemoLoader(path: string): (() => Promise<{ default: React.ComponentType }>) | null {
  return DEMO_MODULES[path] || null;
}
